import React from "react";

import {
    Delete as DeleteIcon,
    Description as DocumentationIcon,
    History as HistoryIcon,
    Info as InfoIcon,
    DataObject as MappingIcon,
    Schedule as ScheduleIcon,
    Settings as SettingsIcon,
    Science as TestIcon,
    Update as UpdateIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import { getSourceDocumentLink } from "helpers/documentation/getDocumentationLinks";

import { GenericModal } from "components/GenericModal";
import { GenericTabs } from "components/GenericTabs";
import SwalAlertComp from "components/SwalAlert";

import DeleteSourceDialog from "./components/DeleteSourceDialog";
import SourceIcon from "./components/SourceIcon";
import EditMappingTab from "./components/tabs/EditMappingTab";
import HistoryTab from "./components/tabs/HistoryTab";
import OverviewTab from "./components/tabs/OverviewTab";
import ReconnectTab from "./components/tabs/ReconnectTab";
import ScheduleTab from "./components/tabs/ScheduleTab";
import SettingsTab from "./components/tabs/SettingsTab";
import TestTab from "./components/tabs/TestTab";
import UpdateTab from "./components/tabs/UpdateTab";
import { useSourceModal } from "./hooks/useSourceModal";
import { SourceModalProps, TabProps, TabType } from "./SourceModal.types";

const openDocumentNewTab = (e: React.MouseEvent, url?: string) => {
    e.preventDefault();
    if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
    }
};

const TAB_ICONS: Record<TabType, React.ReactNode> = {
    overview: <InfoIcon />,
    settings: <SettingsIcon />,
    update: <UpdateIcon />,
    history: <HistoryIcon />,
    test: <TestIcon />,
    editMapping: <MappingIcon />,
    schedule: <ScheduleIcon />,
    reconnect: <UpdateIcon />,
};

const SourceModal: React.FC<SourceModalProps> = ({
    isOpen,
    source,
    tracker,
    language,
    onClose,
    apiDocumentList = [],
    mode = "tracker",
    onRefresh,
    initialTab = null,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const {
        isDeleteDialogOpen,
        availableTabs,
        activeTab,
        setActiveTab,
        openDeleteDialog,
        closeDeleteDialog,
        refreshTracker,
        handleClose,
        getTabLabel,
    } = useSourceModal({
        source,
        tracker,
        onClose,
        onRefresh,
        mode,
        initialTab,
    });

    const sourceCode = source?.source_type?.code;

    const tabProps: TabProps = {
        source,
        tracker,
        language,
        onRefresh: refreshTracker,
        onClose: handleClose,
        mode,
    };

    const renderTabContent = (tabType: TabType): React.ReactNode => {
        switch (tabType) {
            case "overview":
                return <OverviewTab {...tabProps} />;
            case "settings":
                return <SettingsTab {...tabProps} />;
            case "history":
                return <HistoryTab {...tabProps} />;
            case "test":
                return <TestTab {...tabProps} />;
            case "editMapping":
                return <EditMappingTab {...tabProps} />;
            case "update":
                return <UpdateTab {...tabProps} />;
            case "schedule":
                return <ScheduleTab {...tabProps} />;
            case "reconnect":
                return <ReconnectTab {...tabProps} />;
            default:
                return null;
        }
    };

    const isDisconnected = source?.status === 0;
    const isDeleteDisabled =
        mode === "standalone" && (source?.trackers?.length ?? 0) > 0;

    const tabs = availableTabs.map((tabType) => ({
        label: getTabLabel(tabType, language),
        value: tabType,
        content: renderTabContent(tabType),
        icon: TAB_ICONS[tabType],
        disabled: isDisconnected && tabType !== "reconnect",
    }));

    const getDocUrl = (): string | undefined => {
        const dynamicDocSourceTypes = [
            "capi",
            "js",
            "redirect",
            "androidcapi",
            "ioscapi",
        ];

        if (dynamicDocSourceTypes.includes(sourceCode || "")) {
            return apiDocumentList.find((x) => x?.doc_code === sourceCode)
                ?.doc_url;
        } else {
            const staticUrl = getSourceDocumentLink(sourceCode);
            return staticUrl !== "" ? staticUrl : undefined;
        }
    };

    const docUrl = getDocUrl();

    const documentationLabel =
        language?.buttons?.documentation ?? "Documentation";
    const deleteLabel =
        mode === "standalone"
            ? "Delete Source"
            : (language?.buttons?.remove_source ?? "Remove Source");

    const handleDocumentationClick = (e: React.MouseEvent) => {
        if (docUrl) {
            openDocumentNewTab(e, docUrl);
        } else {
            SwalAlertComp({
                title: "Documentation Not Available",
                message: `Documentation for ${
                    source?.source_type?.name || "this source type"
                } is not available yet. Please contact support for assistance.`,
                icon: "info",
                timer: 4000,
            });
        }
    };

    const headerActions = (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, sm: 1 },
            }}
        >
            {docUrl &&
                (isMobile ? (
                    <Tooltip title={documentationLabel} placement="bottom">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={handleDocumentationClick}
                        >
                            <DocumentationIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DocumentationIcon />}
                        onClick={handleDocumentationClick}
                        color="primary"
                        sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}
                    >
                        {documentationLabel}
                    </Button>
                ))}
            <Tooltip
                title={
                    isDeleteDisabled
                        ? (language?.sources?.cannot_delete_in_use ??
                          "This source is in use by a tracker and cannot be deleted.")
                        : ""
                }
                placement="bottom"
            >
                <span>
                    {isMobile ? (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={openDeleteDialog}
                            disabled={isDeleteDisabled}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={openDeleteDialog}
                            disabled={isDeleteDisabled}
                            sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}
                        >
                            {deleteLabel}
                        </Button>
                    )}
                </span>
            </Tooltip>
        </Box>
    );

    return (
        <>
            <GenericModal
                open={isOpen}
                onClose={handleClose}
                title={source?.source_type?.name}
                headerColor="#f5f5f5"
                contentPadding={0}
                icon={
                    <SourceIcon
                        code={sourceCode}
                        size="medium"
                        alt={source?.source_type?.name}
                    />
                }
                content={
                    <GenericTabs
                        tabs={tabs}
                        defaultTab={activeTab}
                        animation="fade"
                        showBadges={false}
                        elevation={0}
                        contentPadding={isMobile ? 2 : 3}
                        variant="scrollable"
                        onTabChange={(value) => setActiveTab(value as TabType)}
                        actionButtons={headerActions}
                    />
                }
                maxWidth="lg"
                fixedHeight="90vh"
            />

            <DeleteSourceDialog
                open={isDeleteDialogOpen}
                onClose={closeDeleteDialog}
                source={source}
                tracker={tracker}
                language={language}
                onDeleteSuccess={handleClose}
                mode={mode}
                onRefresh={onRefresh}
            />
        </>
    );
};

export default SourceModal;
